import {
  createContext,
  ReactNode,
  useLayoutEffect,
  useRef,
  useEffect,
  useContext,
  useCallback,
} from 'react';
import { LottieAnimationMethods } from '../../../LottieAnimation';

import { mapDispatchToProps } from './actions';
import { useAnimationsSectionReducer } from './reducer';
import { ContextInterface } from '../types';

const initialContext: ContextInterface = {
  animationRefs: { current: [] },
  animationRootMargin: '',
  isAnimationScrollable: null,
  isViewportSizeKnown: false,
  prevSectionIndex: null,
  scrollToSection: () => {},
  sectionIndex: 0,
  sectionRefs: { current: [] },
  subNavHeight: 0,
  subNavRef: { current: null },
  textRootMargin: '',
  universalNavHeight: 0,
  updateSectionIndex: () => {},
};

export const Context = createContext<ContextInterface>(initialContext);

export const Provider = ({ children }: { children: ReactNode }) => {
  const sectionRefs = useRef<HTMLDivElement[]>([]);
  const subNavRef = useRef<HTMLDivElement>(null);
  const animationRefs = useRef<LottieAnimationMethods[]>([]);

  const [state, dispatch] = useAnimationsSectionReducer();
  const { sectionIndex, prevSectionIndex } = state;

  const getActions = useCallback(() => {
    return mapDispatchToProps(dispatch);
  }, [dispatch]);

  useLayoutEffect(() => {
    getActions().updateSubNavHeight(
      subNavRef.current?.getBoundingClientRect()?.height || 0,
    );
  }, [getActions]);

  useEffect(() => {
    if (typeof prevSectionIndex !== 'number') return;
    const animation = animationRefs.current[prevSectionIndex];
    if (animation) {
      animation.stop();
    }
  }, [prevSectionIndex]);

  useEffect(() => {
    const animation = animationRefs.current[sectionIndex];
    if (animation) {
      animation.play();
    }

    return () => {
      if (animation) animation.stop();
    };
  }, [sectionIndex]);

  const scrollToSection = (index: number) => {
    sectionRefs.current[index].scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    });
  };

  const actions = getActions();

  return (
    <Context.Provider
      value={{
        animationRefs,
        scrollToSection,
        sectionRefs,
        subNavRef,
        ...actions,
        ...state,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export const useAnimationsSectionContext = () => useContext(Context);
